'use client';

import { createGlobalStyle, DefaultTheme } from 'styled-components';

import { darkTheme } from '@/styles/darkTheme';
import { lightTheme } from '@/styles/lightTheme';

const generateCssVariables = (theme: DefaultTheme) => {
  return Object.entries(theme.palette)
    .map(([section, colors]) =>
      Object.entries(colors)
        .map(([key, value]) => `--${section}-${key}: ${value};`)
        .join('\n')
    )
    .join('\n');
};

const lightThemeCss = generateCssVariables(lightTheme);
const darkThemeCss = generateCssVariables(darkTheme);

export const GlobalStyles = createGlobalStyle`
  :root {
    --px-sm: 16px;
    --px-md: 40px;
    --px-xl: 80px;
    ${lightThemeCss};
    --custom-yellow: var(--system-yellow);
    --primary-hover: var(--system-black);
    --title-snackbar: var(--textAndLabels-main);
    --switcher-open: var(--textAndLabels-main);
    --delete-button-outline: var(--system-black);
    --delete-button-hover: #E4E2E2;
    --promote-button-text: var(--bg-main);
    --card-chip-border: var(--border-secondary);
    --selected-chip-border: var(--textAndLabels-main);
    --bookmark-fill: transparent;
    --bookmark-hover-fill: #E4E2E2;
    --header-scrolled-bg: rgba(252, 252, 252, 0.5);
    --selected-chip-bg: var(--bg-tertiary);
    --favourite-fill: transparent;
    --favourite-hover-fill: #E4E2E2;
    --contact-button-bg: #75D389;
    --contact-button-text: #282828;
    --contact-button-border: #282828;
    --contact-button-bg-hover: #96EAA8;
    --toast-bg-main: #2F2F2FE5;
    --toast-text-main: #FCFCFC;
    --toast-border-main: #989898;
    --resume-price-bg: rgba(218, 232, 255, 1);
    --dashboard-vacancy-icon: var(--bg-main);
    --dashboard-resume-icon: #FFE46F;
    --highlight-text-color: #FFB900;
    --no-data-dashboard: var(--textAndLabels-dark);
    --pay-button-hover: #309F50;
    --pay-button-text-color:#FCFCFC;
    --ad-card-premium-color:#c8ef01;
    --publish-ad-modal-bg:var(--brand-main);
    --dropdown-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.15);
    --account-balance-bg: #E2EBED;
    --account-balance-text-color:#2F2F2F;
  }

  [data-theme="dark"] {
    ${darkThemeCss};
    --custom-yellow: var(--bg-tertiary);
    --primary-hover: var(--bg-tertiary);
    --title-snackbar: var(--bg-main);
    --switcher-open: var(--bg-tertiary);
    --delete-button-outline: var(--textAndLabels-main);
    --delete-button-hover: #2a2a2a;
    --promote-button-text: var(--textAndLabels-main);
    --card-chip-border: var(--border-main);
    --selected-chip-border: var(--system-white);
    --bookmark-fill: transparent;
    --bookmark-hover-fill: #000;
    --header-scrolled-bg: rgba(40, 40, 40, 0.5);
    --selected-chip-bg: var(--bg-secondary);
    --favourite-fill: transparent;
    --favourite-hover-fill: #000;
    --contact-button-bg: #67E683;
    --contact-button-text: #282828;
    --contact-button-border: #15AD36;
    --contact-button-bg-hover: #8BFDA4;
    --toast-bg-main: #FCFCFCE5;
    --toast-text-main: #282828;
    --toast-border-main: #A6A6A6;
    --resume-price-bg: rgba(227, 238, 255, 1);
    --dashboard-vacancy-icon: #2E2E2E;
    --dashboard-resume-icon: #FFF3C1;
    --highlight-text-color: var(--bg-tertiary);
    --no-data-dashboard: var(--textAndLabels-main);
    --pay-button-hover: #66C281;
    --pay-button-text-color:#FCFCFC;
    --ad-card-premium-color:#c8ef01;
    --publish-ad-modal-bg:var(--system-brand);
    --account-balance-bg: #E2EBED;
    --account-balance-text-color:#282828;
  }

  @font-face {
    font-family: 'Helvetica Neue';
    src: font-url('./fonts/HelveticaNeue.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Helvetica Neue';
    src: font-url('./fonts/HelveticaNeue-Medium.woff2') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Helvetica Neue';
    src: font-url('./fonts/HelveticaNeue-Bold.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    text-size-adjust: none;
    -webkit-backface-visibility: hidden;
  }

  body, button, input, textarea, select {
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 400;
    line-height: 1.2;
  }

  body {
    min-height: 100dvh;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
    background: var(--bg-main);

    &.ios {
      overscroll-behavior: none;
    }

    &::-webkit-scrollbar{
      width: 0;
      height: 0;
    }
  }

  img {
    max-width: 100%;
    font-style: italic;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:focus {
    transition: background-color 600000s 0s, color 600000s 0s;
  }

  input[type='search']::-webkit-search-decoration,
  input[type='search']::-webkit-search-cancel-button,
  input[type='search']::-webkit-search-results-button,
  input[type='search']::-webkit-search-results-decoration {
      display: none;
  }

  input[type=file] {
    display: none;
  }
`;
